import { ChangeEvent, useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  TextField,
  MenuItem,
  Select,
  Button,
  CardMedia,
  IconButton,
  Input,
  FormHelperText,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { LocalizationProvider, DatePicker, DateTimePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import EditText from "../../components/editText";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import dayjs, { Dayjs } from "dayjs";
import { PROMOTION_REWARD_TYPE, REWARD_CATEGORY, REWARD_TYPE } from "../../constants/enums";
import { UploadMedia } from "../../utils/uploadMedia";
import { isNumber } from "../../utils/validations";
import { useAddPromotionMutation, useGetPromotionByIdQuery, useUpdatePromotionMutation } from "../../services/promotions";
import usePermission from "../../hooks/userAccess";

const AddRewards = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const { isAdd } = usePermission({
    label: 'Rewards & Offers Management',
    page_permission: 'isAdd',
  });

  const { data: promoRes, isLoading: isFetching } = useGetPromotionByIdQuery(id ? { id } : ({ id: '' } as any), { skip: !id });

  const [addPromotion, { isLoading: isSaving }] = useAddPromotionMutation();
  const [updatePromotion, { isLoading: isUpdating }] = useUpdatePromotionMutation();

  // Rich text state and file name
  const [fileName, setFileName] = useState<string>("");
  // Description fields now handled directly by Formik values
  const [legal2, setLegal2] = useState<string>("");
  const [legal2Ar, setLegal2Ar] = useState<string>("");
  const [alertMessage, setAlertMessage] = useState("");
  const [showAlert, setShowAlert] = useState(false);
  const [alertType, setAlertType] = useState(0);

  // Initialize initialValues based on promoRes or defaults
  const initialValues = {
    image: promoRes?.data?.image || "",
    title: promoRes?.data?.title || "",
    title_ar: promoRes?.data?.title_ar || "",
    rewardType: promoRes?.data?.rewardType || "default",
    discountValue: promoRes?.data?.discountValue || "",
    cashbackValue: promoRes?.data?.cashbackValue || "",
    loyaltyPoints: promoRes?.data?.loyaltyPoints || "",
    startFrom: promoRes?.data?.startFrom || null,
    expireAt: promoRes?.data?.expireAt || null,
    ctaLabel: promoRes?.data?.ctaLabel || "",
    maxRedemptions: promoRes?.data?.maxRedemptions || "",
    category: promoRes?.data?.category || "default",
    description: promoRes?.data?.description || "",
    description_ar: promoRes?.data?.description_ar || "",
    termsConditions: promoRes?.data?.termsConditions || "",
    termsConditions_ar: promoRes?.data?.termsConditions_ar || "",
  };

  // Prefill rich text fields and fileName when promoRes is available
  useEffect(() => {
    if (promoRes) {
      setLegal2(promoRes?.data?.termsConditions || "");
      setLegal2Ar(promoRes?.data?.termsConditions_ar || "");
      setFileName(promoRes?.data?.image ? "Uploaded Image" : ""); // Set a default file name or extract from URL if needed
    }
  }, [promoRes]);

  const validationSchema = Yup.object({
    image: Yup.string().required("Image is required"),
    title: Yup.string().required("Reward Title is required"),
    title_ar: Yup.string().required("Arabic Reward Title is required"),
    rewardType: Yup.string().notOneOf(["default"], "Please select a reward type"),
    description: Yup.string()
      .required("Description is required")
      .min(10, "Description must be at least 10 characters"),
    description_ar: Yup.string()
      .required("Arabic Description is required")
      .min(10, "Arabic Description must be at least 10 characters"),
    termsConditions: Yup.string()
      .required("Terms & Conditions are required")
      .min(20, "Terms & Conditions must be at least 20 characters"),
    termsConditions_ar: Yup.string()
      .required("Arabic Terms & Conditions are required")
      .min(20, "Arabic Terms & Conditions must be at least 20 characters"),
    discountValue: Yup.number().when("rewardType", {
      is: `${REWARD_TYPE.DISCOUNT}`,
      then: (schema) => schema.required("Discount value is required").min(1),
    }),
    cashbackValue: Yup.number().when("rewardType", {
      is: `${REWARD_TYPE.CASHBACK}`,
      then: (schema) => schema.required("Cashback amount is required").min(1),
    }),
    loyaltyPoints: Yup.number().required("Loyalty points required").min(1),
    startFrom: Yup.date().required("Valid start date is required"),
    expireAt: Yup.date().required("Valid end date is required"),
    ctaLabel: Yup.string().required("CTA Label is required"),
    maxRedemptions: Yup.number().required("Max redemption is required").min(1),
    category: Yup.string().notOneOf(["default"], "Please select a category"),
  });

  const handleSubmit = async (values: typeof initialValues) => {
    const body = {
      ...values,
      description: values.description,
      description_ar: values.description_ar,
      termsConditions: legal2,
      termsConditions_ar: legal2Ar,
      cashbackValue: values.cashbackValue ? Number(values.cashbackValue) : 0,
      discountValue: values.discountValue ? Number(values.discountValue) : 0,
      type: PROMOTION_REWARD_TYPE.REWARDS,
    };
    try {
      if (!legal2?.length || !values.description?.length || !values.description_ar?.length || !legal2Ar?.length) {
        setAlertType(0);
        setShowAlert(true);
        setAlertMessage("English & Arabic Description and Terms & Conditions are required.");
        return;
      }

      if (id) {
        await updatePromotion({ body: { ...body, id } as any }).unwrap();
      } else {
        await addPromotion(body as any).unwrap();
      }

      navigate(-1);
    } catch (error: any) {
      setAlertType(0);
      setShowAlert(true);
      setAlertMessage("An error occurred while saving. Please try again.");
    }
  };

  const handleImageUpload = async (file: any, setFieldValue: any) => {
    const res = await UploadMedia(file);
    const imageUrl = res?.data?.images?.[0]?.url || res?.data?.image || "";
    if (imageUrl) {
      setFieldValue('image', imageUrl);
      setFileName(file.name); // Update fileName on new upload
    }
  };

  // Optional: Show a loading state while fetching promoRes
  if (isFetching && id) {
    return <div>Loading...</div>;
  }

  return (
    <>
      <div className="main_title">
        <h1>{id ? "Edit Reward" : "Add Rewards & Offers"}</h1>
        <p>
          <button onClick={() => navigate("/dashboard")}>Dashboard</button> -{" "}
          <button onClick={() => navigate("/manage-rewards")}>
            Rewards & Offers Management
          </button>{" "}
          - {id ? "Edit Reward" : "Add Rewards & Offers"}
        </p>
      </div>

      <div className="cards">
        <Formik
          enableReinitialize // Required to update initialValues when promoRes changes
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ values, errors, touched, handleChange, setFieldValue, setFieldTouched }) => (
            <Form className="form">
              <div className="gap_p">
                {/* Banner Image */}
                <div className="control_group w_100">
                  <div className="gap_p">
                    <div className="control_group w_33">
                      <label htmlFor="icon-button-file">
                        Upload Image <span style={{ color: 'red' }}>*</span>
                      </label>
                      {values.image ? (
                        <div className="upload_image v2 no_radius">
                          <div className="upload_image_holder">
                            <figure>
                              <CardMedia
                                component="img"
                                image={values.image}
                                alt="Uploaded promotion image"
                              />
                            </figure>
                            <IconButton
                              aria-label="Remove uploaded image"
                              onClick={() => {
                                setFieldValue('image', '');
                                setFileName("");
                              }}
                              size="small"
                            >
                              <CloseIcon />
                            </IconButton>
                          </div>
                        </div>
                      ) : (
                        <label
                          className="upload_image v2 no_radius"
                          htmlFor="icon-button-file"
                        >
                          <Input
                            sx={{ display: "none" }}
                            id="icon-button-file"
                            type="file"
                            inputProps={{ accept: "image/png,image/jpeg,image/jpg" }}
                            onChange={(event: ChangeEvent<HTMLInputElement>) => {
                              const files = (event.target as HTMLInputElement).files;
                              if (files && files[0]) {
                                if (files[0].type.includes("image")) {
                                  if (files[0].size > 5 * 1024 * 1024) {
                                    setAlertType(0);
                                    setShowAlert(true);
                                    setAlertMessage("Image size must be less than 5MB.");
                                    return;
                                  }
                                  setFileName(files[0].name);
                                  handleImageUpload(files[0], setFieldValue);
                                } else {
                                  setAlertType(0);
                                  setShowAlert(true);
                                  setAlertMessage("This field only accepts images (PNG, JPEG, JPG).");
                                }
                              }
                            }}
                          />
                          <span className="upload_image_holder">
                            <figure className="add_img">
                              <img
                                src="/static/images/placeholder.png"
                                alt="placeholder"
                              />
                              <span className="upload_img_icon">
                                <img
                                  src="/static/images/edit2_icon.svg"
                                  alt="Upload"
                                />
                              </span>
                            </figure>
                          </span>
                        </label>
                      )}
                      {touched.image && errors.image && (
                        <FormHelperText error>{String(errors.image)}</FormHelperText>
                      )}
                    </div>
                  </div>
                </div>

                {/* Title */}
                <div className="control_group w_50">
                  <label htmlFor="title">Reward Title <span style={{ color: 'red' }}>*</span></label>
                  <TextField
                    id="title"
                    name="title"
                    placeholder="Enter Reward Title"
                    fullWidth
                    value={values.title}
                    onChange={handleChange}
                    error={touched.title && Boolean(errors.title)}
                    helperText={touched.title ? String(errors.title) : ''}
                  />
                </div>

                {/* Arabic Title */}
                <div className="control_group w_50">
                  <label htmlFor="title_ar">Arabic Reward Title <span style={{ color: 'red' }}>*</span></label>
                  <TextField
                    id="title_ar"
                    name="title_ar"
                    placeholder="Enter Arabic Reward Title"
                    fullWidth
                    value={values.title_ar}
                    onChange={handleChange}
                    error={touched.title_ar && Boolean(errors.title_ar)}
                    helperText={touched.title_ar ? String(errors.title_ar) : ''}
                  />
                </div>

                {/* Type */}
                <div className="control_group w_50">
                  <div className="gap_p">
                    <div
                      className={`control_group ${values.rewardType === "default" ? "w_100" : "w_50"}`}
                    >
                      <label htmlFor="type">Reward Type <span style={{ color: 'red' }}>*</span></label>
                      <Select
                        id="rewardType"
                        name="rewardType"
                        fullWidth
                        value={values.rewardType}
                        onChange={handleChange}
                        error={touched.rewardType && Boolean(errors.rewardType)}
                      >
                        <MenuItem disabled value="default">
                          Select Type
                        </MenuItem>
                        <MenuItem value={REWARD_TYPE.DISCOUNT}>Discount</MenuItem>
                        <MenuItem value={REWARD_TYPE.CASHBACK}>Cashback</MenuItem>
                      </Select>
                    </div>
                    {touched.rewardType && errors.rewardType && (
                      <FormHelperText error>{String(errors.rewardType)}</FormHelperText>
                    )}

                    {`${values.rewardType}` === `${REWARD_TYPE.DISCOUNT}` && (
                      <div className="control_group w_50">
                        <label htmlFor="discountValue">Discount Value (%) <span style={{ color: 'red' }}>*</span></label>
                        <TextField
                          id="discountValue"
                          name="discountValue"
                          onChange={(val) => {
                            if (isNumber(val.target.value)) {
                              handleChange(val);
                            }
                          }}
                          placeholder="Enter Discount Percentage"
                          fullWidth
                          value={values.discountValue}
                          error={touched.discountValue && Boolean(errors.discountValue)}
                          helperText={touched.discountValue ? String(errors.discountValue) : ''}
                        />
                      </div>
                    )}

                    {`${values.rewardType}` === `${REWARD_TYPE.CASHBACK}` && (
                      <div className="control_group w_50">
                        <label htmlFor="cashbackValue">Cashback Amount <span style={{ color: 'red' }}>*</span></label>
                        <TextField
                          id="cashbackValue"
                          name="cashbackValue"
                          onChange={(val) => {
                            if (isNumber(val.target.value)) {
                              handleChange(val);
                            }
                          }}
                          placeholder="Enter Cashback Amount"
                          fullWidth
                          value={values.cashbackValue}
                          error={touched.cashbackValue && Boolean(errors.cashbackValue)}
                          helperText={touched.cashbackValue ? String(errors.cashbackValue) : ''}
                        />
                      </div>
                    )}
                  </div>
                </div>

                {/* Loyalty Points */}
                <div className="control_group w_33">
                  <label htmlFor="loyaltyPoints">Loyalty Points Required <span style={{ color: 'red' }}>*</span></label>
                  <TextField
                    id="loyaltyPoints"
                    name="loyaltyPoints"
                    placeholder="Enter Points"
                    fullWidth
                    value={values.loyaltyPoints}
                    onChange={(val) => {
                      if (isNumber(val.target.value)) {
                        handleChange(val);
                      }
                    }}
                    error={touched.loyaltyPoints && Boolean(errors.loyaltyPoints)}
                    helperText={touched.loyaltyPoints ? String(errors.loyaltyPoints) : ''}
                  />
                </div>

                {/* Validity Period */}
                <div className="control_group w_33">
                  <label htmlFor="startFrom">Valid Start Date <span style={{ color: 'red' }}>*</span></label>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DateTimePicker
                      className="form_control"
                      slots={{
                        openPickerIcon: () => <img src="/static/images/date_icon.svg" alt="Pick date" />,
                      }}
                      slotProps={{
                        textField: {
                          id: "startFrom",
                          placeholder: "Select Start Date & Time",
                          fullWidth: true,
                          hiddenLabel: true,
                          error: touched.startFrom && Boolean(errors.startFrom),
                          helperText: touched.startFrom && errors.startFrom ? String(errors.startFrom) : "",
                        },
                      }}
                      desktopModeMediaQuery="(min-width:0px)"
                      value={values.startFrom ? dayjs(values.startFrom) : null}
                      onChange={(v) => setFieldValue('startFrom', v ? (v as Dayjs).toISOString() : '')}
                      minDateTime={dayjs()}
                    />
                  </LocalizationProvider>
                </div>
                <div className="control_group w_33">
                  <label htmlFor="expireAt">Valid End Date <span style={{ color: 'red' }}>*</span></label>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DateTimePicker
                      className="form_control"
                      slots={{
                        openPickerIcon: () => <img src="/static/images/date_icon.svg" alt="Pick date" />,
                      }}
                      slotProps={{
                        textField: {
                          id: "expireAt",
                          placeholder: "Select End Date & Time",
                          fullWidth: true,
                          hiddenLabel: true,
                          error: touched.expireAt && Boolean(errors.expireAt),
                          helperText: touched.expireAt && errors.expireAt ? String(errors.expireAt) : "",
                        },
                      }}
                      desktopModeMediaQuery="(min-width:0px)"
                      value={values.expireAt ? dayjs(values.expireAt) : null}
                      onChange={(v) => setFieldValue('expireAt', v ? (v as Dayjs).toISOString() : '')}
                      minDateTime={values.startFrom ? dayjs(values.startFrom).add(1, 'hour') : dayjs().add(1, 'hour')}
                    />
                  </LocalizationProvider>
                </div>

                {/* CTA Label */}
                <div className="control_group w_33">
                  <label htmlFor="ctaLabel">CTA Label <span style={{ color: 'red' }}>*</span></label>
                  <TextField
                    id="ctaLabel"
                    name="ctaLabel"
                    placeholder="e.g., Redeem Now"
                    fullWidth
                    value={values.ctaLabel}
                    onChange={handleChange}
                    error={touched.ctaLabel && Boolean(errors.ctaLabel)}
                    helperText={touched.ctaLabel ? String(errors.ctaLabel) : ''}
                  />
                </div>

                {/* Max Redemption */}
                <div className="control_group w_33">
                  <label htmlFor="maxRedemptions">
                    Max Redemption / Voucher Limit per user <span style={{ color: 'red' }}>*</span>
                  </label>
                  <TextField
                    id="maxRedemptions"
                    name="maxRedemptions"
                    placeholder="Enter Limit"
                    fullWidth
                    value={values.maxRedemptions}
                    onChange={(val) => {
                      if (isNumber(val.target.value)) {
                        handleChange(val);
                      }
                    }}
                    error={touched.maxRedemptions && Boolean(errors.maxRedemptions)}
                    helperText={touched.maxRedemptions ? String(errors.maxRedemptions) : ''}
                  />
                </div>

                {/* Category */}
                <div className="control_group w_33">
                  <label htmlFor="category">Assign to Category <span style={{ color: 'red' }}>*</span></label>
                  <Select
                    id="category"
                    name="category"
                    fullWidth
                    value={values.category}
                    onChange={handleChange}
                    error={touched.category && Boolean(errors.category)}
                  >
                    <MenuItem disabled value="default">
                      Select Category
                    </MenuItem>
                    <MenuItem value={REWARD_CATEGORY.BOOKS}>Books</MenuItem>
                    <MenuItem value={REWARD_CATEGORY.UNIFORM}>Uniforms</MenuItem>
                    <MenuItem value={REWARD_CATEGORY.STATIONARY}>Stationary</MenuItem>
                  </Select>
                  {touched.category && errors.category && (
                    <FormHelperText error>{String(errors.category)}</FormHelperText>
                  )}
                </div>

                {/* Description */}
                <div className="control_group w_100">
                  <label htmlFor="description">Description <span style={{ color: 'red' }}>*</span></label>
                  <TextField
                    id="description"
                    name="description"
                    placeholder="Enter Description"
                    fullWidth
                    multiline
                    minRows={4}
                    value={values.description}
                    onChange={handleChange}
                    error={touched.description && Boolean(errors.description)}
                    helperText={touched.description ? String(errors.description) : ''}
                  />
                </div>

                {/* Arabic Description */}
                <div className="control_group w_100">
                  <label htmlFor="description_ar">Arabic Description <span style={{ color: 'red' }}>*</span></label>
                  <TextField
                    id="description_ar"
                    name="description_ar"
                    placeholder="Enter Arabic Description"
                    fullWidth
                    multiline
                    minRows={4}
                    value={values.description_ar}
                    onChange={handleChange}
                    error={touched.description_ar && Boolean(errors.description_ar)}
                    helperText={touched.description_ar ? String(errors.description_ar) : ''}
                  />
                </div>

                {/* Terms & Conditions */}
                <div className="control_group w_100">
                  <label htmlFor="reward_terms">Terms & Conditions <span style={{ color: 'red' }}>*</span></label>
                  {isEdit && !legal2 ? null : <EditText content={legal2} setContent={(val: string) => { setLegal2(val); setFieldValue('termsConditions', val); }} />}
                  {touched.termsConditions && errors.termsConditions && (
                    <FormHelperText error>{String(errors.termsConditions)}</FormHelperText>
                  )}
                </div>

                {/* Arabic Terms & Conditions */}
                <div className="control_group w_100">
                  <label htmlFor="reward_terms_ar">Arabic Terms & Conditions <span style={{ color: 'red' }}>*</span></label>
                  {isEdit && !legal2Ar ? null : <EditText content={legal2Ar} setContent={(val: string) => { setLegal2Ar(val); setFieldValue('termsConditions_ar', val); }} />}
                  {touched.termsConditions_ar && errors.termsConditions_ar && (
                    <FormHelperText error>{String(errors.termsConditions_ar)}</FormHelperText>
                  )}
                </div>
              </div>

              {isAdd && <div className="form_btn">
                <Button disabled={isSaving || isUpdating} className="btn btn_primary" type="submit">
                  {id ? "Update" : "Submit"}
                </Button>
              </div>}
            </Form>
          )}
        </Formik>
      </div>
    </>
  );
};

export default AddRewards;